import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  COUNTRY_CODES,
  contactLabel,
  contactPhrase,
  CURRENCY_SYMBOL,
  D,
  defaultDepositCurrency,
  DEPOSIT_CURRENCIES,
  DEPOSIT_MIN_USD,
  fmt,
  fmtUsd,
  formatCallTime,
  isRestrictedCountry,
  MAX_UPLOAD_BYTES,
  RESTRICTED_COUNTRY_NAMES,
  soonestCall,
  type CryptoDepositConfig,
  type ComplianceState,
  type DepositCurrency,
  type DepositRequest,
  type Profile,
} from '@platform/shared';
import { NumInput } from '../../components/NumInput';
import { cx, Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { IP_COUNTRY } from '../../lib/config';
import { useSearch } from '../../lib/router';
import { useCompliance, useLoad, useVersion } from '../../stores/account';
import { useMe } from '../../stores/session';
import { BankPicker, OTHER_BANK } from './deposit/BankPicker';
import { CallPicker, clientTimeZone, type CallChoice } from './deposit/CallPicker';
import { NowPaymentsDeposit } from './deposit/NowPaymentsDeposit';
import { accountLock, DepositLocked } from '../onboarding/AccountLock';
import { AIcon, Alert, Card, Chip, errMsg, Field, H1, H2, List, Notice, PillButton, PillLink, Row, Segmented, when } from './kit';

type Tab = 'crypto' | 'wire' | 'card';
const tabFrom = (search: string): Tab => {
  const m = new URLSearchParams(search).get('method');
  return m === 'wire' || m === 'other' ? 'wire' : m === 'card' ? 'card' : 'crypto';
};

const countryNames = (() => {
  let dn: Intl.DisplayNames | null = null;
  try {
    dn = new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    /* old browser */
  }
  return COUNTRY_CODES.map((c) => ({ code: c, name: dn?.of(c) ?? c })).sort((a, b) => a.name.localeCompare(b.name));
})();
const countryName = (cc: string) => countryNames.find((c) => c.code === cc)?.name ?? cc;

export function DepositPage() {
  const search = useSearch();
  const [tab, setTab] = useState<Tab>(() => tabFrom(search));
  const { data: profile, loading } = useLoad<Profile>('/api/profile');
  const me = useMe();
  if (accountLock(me)) return <DepositLocked />;
  if (loading && !profile) return <Spinner />;
  if (isRestrictedCountry(profile?.country)) {
    return (
      <Card className="flex max-w-[760px] flex-col items-start gap-4">
        <Chip tone="down">Deposits unavailable</Chip>
        <H1>We can't accept deposits from clients in {countryName(profile!.country!)}</H1>
        <p className="m-0 text-[16px] text-muted md:text-[17px]">
          Our services aren't available to residents of {RESTRICTED_COUNTRY_NAMES}, so deposits are switched off for your account. If your country of residence has changed, update it in your profile and
          our team will review it.
        </p>
        <PillLink to="/account/support?topic=funding" kind="secondary">
          Contact support
        </PillLink>
      </Card>
    );
  }
  // Country and currency default from where the client is connecting (IP), then their profile.
  const home = IP_COUNTRY || profile?.country || '';
  return (
    <div className="flex flex-col gap-6">
      <ThresholdNotice />
      <Segmented
        label="Deposit method"
        size="lg"
        className="w-full max-w-[580px]"
        options={[
          { id: 'crypto', label: 'Crypto' },
          { id: 'wire', label: 'Wire transfer' },
          { id: 'card', label: 'Card' },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === 'crypto' ? <CryptoTab /> : <RequestDeposit key={tab} method={tab === 'wire' ? 'bank_wire' : 'card'} home={home} />}
    </div>
  );
}

/**
 * Deposits above the threshold need an approved source of funds. Clients hear nothing about it until
 * their deposits reach the threshold; then this asks for the bank statements.
 */
function ThresholdNotice() {
  const c = useCompliance();
  if (!c || c.enhancedVerified) return null;
  const total = Number(c.lifetimeDeposits) + Number(c.pendingDeposits);
  if (c.sofStatus === 'pending') {
    return <Alert tone="info">We are reviewing your source of funds. Once it is approved, deposits above {fmtUsd(c.threshold, 0)} in total go through as usual.</Alert>;
  }
  // Nothing about source of funds before the client's deposits reach the threshold.
  if (!c.sofRequired) return null;
  const pct = Math.min(100, (total / Number(c.threshold)) * 100);
  return (
    <div className="flex flex-col gap-3 rounded-[20px] bg-alert p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2 text-[15px] md:text-[16px]">
        <b>We need your source of funds</b>
        <span className="num text-muted">
          {fmtUsd(total)} / {fmtUsd(c.threshold, 0)}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-track">
        <i className="block h-full rounded-full bg-warn" style={{ width: `${pct}%` }} />
      </div>
      <p className="m-0 text-[15px] text-muted">
        Your total deposits passed {fmtUsd(c.threshold, 0)}. Send 3 months of bank statements so we can approve where your funds come from.
      </p>
      <PillLink to="/account/source-of-funds" className="w-max">
        Add source of funds
      </PillLink>
    </div>
  );
}

const dayName = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

/** Inline compliance notices on the wire / card form: a stale proof of address, or an amount past the threshold. */
function ComplianceChecks({ c, usd, poa, onPoa, onAmount, onError }: { c: ComplianceState; usd: number; poa: File | null; onPoa: (f: File | null) => void; onAmount: (usd: number) => void; onError: (t: string) => void }) {
  const kycApproved = useMe()?.kycStatus === 'approved';
  const needsPoa = kycApproved && c.poaStale && c.poaStatus !== 'pending';
  const headroom = Number(c.headroom);
  const overLimit = !c.enhancedVerified && c.sofStatus !== 'pending' && usd > headroom;
  return (
    <>
      {needsPoa && (
        <div className="flex flex-col gap-3 rounded-[20px] bg-alert p-5">
          <b className="text-[15px] md:text-[16px]">Please update your proof of address</b>
          <span className="text-[15px] text-muted">
            {c.poaStatus === 'rejected' ? 'Your last one could not be accepted.' : `The one we have${c.poaIssuedOn ? ` is dated ${dayName(c.poaIssuedOn)} and` : ''} is more than 3 months old.`} Upload a
            utility bill, bank statement or official letter from the last 3 months, showing your name and address.
          </span>
          <label className="flex cursor-pointer items-center gap-4 rounded-[16px] border border-dashed border-line-strong bg-card p-4 text-[15px] text-muted hover:text-fg">
            <span className={cx('grid size-10 shrink-0 place-items-center rounded-full', poa ? 'bg-up-bg text-up' : 'bg-track')}>
              <AIcon name={poa ? 'check' : 'upload'} />
            </span>
            <span className="min-w-0 flex-1 truncate">{poa ? poa.name : 'Proof of address · JPG, PNG or PDF, 10 MB'}</span>
            <input
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                if (f && f.size > MAX_UPLOAD_BYTES) return onError('The file must be 10 MB or smaller');
                onPoa(f);
              }}
            />
          </label>
        </div>
      )}
      {overLimit && (
        <div className="flex flex-col gap-3 rounded-[20px] bg-alert p-5">
          <b className="text-[15px] md:text-[16px]">{c.enforced ? "We can't take this deposit yet" : 'This deposit needs extra verification'}</b>
          <span className="text-[15px] text-muted">
            It would bring your total deposits to about {fmtUsd(Number(c.lifetimeDeposits) + Number(c.pendingDeposits) + usd)}. Above {fmtUsd(c.threshold, 0)} we need your source of funds
            approved first. This usually takes 1 business day after you upload.
          </span>
          <div className="flex flex-wrap items-center gap-3">
            <PillLink to="/account/source-of-funds" size="sm">
              Upload bank statements
            </PillLink>
            {headroom > 0 && (
              <button type="button" className="text-[15px] font-semibold" onClick={() => onAmount(Math.floor(headroom * 100) / 100)}>
                Deposit up to {fmtUsd(headroom)} instead
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/** True when the form must not be sent yet (only while the rules are enforced). */
function complianceBlocks(c: ComplianceState | null, usd: number, poa: File | null, kycApproved: boolean) {
  if (!c?.enforced) return false;
  const needsPoa = kycApproved && c.poaStale && c.poaStatus !== 'pending' && !poa;
  const overLimit = !c.enhancedVerified && c.sofStatus !== 'pending' && usd > Number(c.headroom);
  return needsPoa || overLimit;
}

/** Crypto deposits go through NOWPayments; until the server has its keys the page says so. */
const OFF: CryptoDepositConfig = { enabled: false, minUsd: '250', coins: [] };
function CryptoTab() {
  const { data: cfg, loading, error } = useLoad<CryptoDepositConfig>('/api/deposits/crypto/config');
  if (!cfg && loading && !error) return <Spinner />;
  return <NowPaymentsDeposit cfg={cfg ?? OFF} />;
}

// ---- wire transfer and card: the client books a call, staff send details or a payment link, then credit it ----

const money = (amount: string, ccy: string) => {
  const sym = CURRENCY_SYMBOL[ccy as DepositCurrency];
  return sym ? `${sym}${fmt(amount, 2)}` : `${fmt(amount, 2)} ${ccy}`;
};

const REQUEST_STATUS: Record<DepositRequest['status'], [string, 'warn' | 'accent' | 'up' | 'down' | 'muted']> = {
  pending: ['Call booked', 'accent'],
  processing: ['Processing', 'accent'],
  details_sent: ['Awaiting your transfer', 'warn'],
  link_sent: ['Payment link sent', 'warn'],
  proof_uploaded: ['Proof received, checking', 'accent'],
  credited: ['Completed', 'up'],
  rejected: ['Rejected', 'down'],
  cancelled: ['Cancelled', 'muted'],
};

const STEPS: Record<'bank_wire' | 'card', [string, string][]> = {
  bank_wire: [
    ['You send this request', 'Support and your account manager are told straight away.'],
    ['We call you at your chosen time', 'Your account manager gives you our bank details. You also get them in your notifications.'],
    ['You make the transfer and upload proof', 'Use "Upload proof of payment" on your request below.'],
    ['We credit your live account in USDT', 'Usually 1 to 3 business days after you send it.'],
  ],
  card: [
    ['You send this request', 'Support and your account manager are told straight away.'],
    ['We contact you at your chosen time', 'And send a secure payment link to your notifications and email.'],
    ['We credit your live account in USDT', 'As soon as the card payment is approved.'],
  ],
};

type Rates = Record<DepositCurrency, string | null>;

function RequestDeposit({ method, home }: { method: 'bank_wire' | 'card'; home: string }) {
  const wire = method === 'bank_wire';
  const tz = useMemo(clientTimeZone, []);
  const [country, setCountry] = useState(home);
  const [currency, setCurrency] = useState<DepositCurrency>(() => defaultDepositCurrency(home));
  const [bank, setBank] = useState('');
  const [otherBank, setOtherBank] = useState('');
  const [amount, setAmount] = useState('');
  const [call, setCall] = useState<CallChoice>(() => ({ callAt: soonestCall(), method: 'phone' }));
  const [timeTouched, setTimeTouched] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [poa, setPoa] = useState<File | null>(null);
  const compliance = useCompliance();
  const kycApproved = useMe()?.kycStatus === 'approved';
  const { data: rates } = useLoad<Rates>('/api/deposit-requests/rates');
  const v = useVersion('depositRequest');
  const { data: requests, reload } = useLoad<DepositRequest[]>('/api/deposit-requests', [v]);

  // Until the client picks a time, the default stays "15 minutes from now".
  useEffect(() => {
    if (timeTouched) return;
    const id = setInterval(() => setCall((c) => ({ ...c, callAt: soonestCall() })), 30_000);
    return () => clearInterval(id);
  }, [timeTouched]);

  const restricted = isRestrictedCountry(country);
  const rate = rates?.[currency] ?? null;
  const amt = Number(amount) || 0;
  const usd = currency === 'USD' ? amt : rate ? amt * Number(rate) : null;
  const min = Number(DEPOSIT_MIN_USD[method] ?? 0);
  const belowMin = amt > 0 && (usd ?? amt) < min;
  const bankName = bank === OTHER_BANK ? otherBank.trim() : bank;
  const ready = !!country && !restricted && amt > 0 && !belowMin && (!wire || !!bankName) && !complianceBlocks(compliance, usd ?? amt, poa, kycApproved);

  const pickCountry = (cc: string) => {
    setCountry(cc);
    setCurrency(defaultDepositCurrency(cc));
    setBank('');
    setOtherBank('');
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy('submit');
    setMsg(null);
    try {
      const callAt = timeTouched ? call.callAt : soonestCall();
      const body: Record<string, string> = {
        method,
        country,
        currency,
        amount,
        ...(wire ? { bank_name: bankName } : {}),
        contact_method: call.method,
        call_at: new Date(callAt).toISOString(),
        call_tz: tz,
      };
      let r: DepositRequest;
      if (poa) {
        // A new proof of address goes with the request (multipart).
        const fd = new FormData();
        for (const [k, val] of Object.entries(body)) fd.append(k, val);
        fd.append('poa', poa, poa.name);
        r = await api.upload<DepositRequest>('/api/deposit-requests', fd);
      } else {
        r = await api.post<DepositRequest>('/api/deposit-requests', body);
      }
      setAmount('');
      setPoa(null);
      setTimeTouched(false);
      setCall((c) => ({ ...c, callAt: soonestCall() }));
      const at = formatCallTime(Date.parse(r.callAt!), tz).replace(' · ', ' at ');
      setMsg({
        ok: true,
        text: `${r.number} sent. Your account manager will ${contactPhrase(r.contactMethod)} on ${at} (your time) ${wire ? 'with our bank details' : 'and send you a secure payment link'}.`,
      });
      void reload();
    } catch (x) {
      setMsg({ ok: false, text: errMsg(x) });
    } finally {
      setBusy(null);
    }
  };

  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id);
    setMsg(null);
    try {
      await fn();
      void reload();
    } catch (x) {
      setMsg({ ok: false, text: errMsg(x) });
    } finally {
      setBusy(null);
    }
  };
  const uploadProof = (id: string, f: File) => {
    if (f.size > MAX_UPLOAD_BYTES) return setMsg({ ok: false, text: 'The file must be 10 MB or smaller' });
    const fd = new FormData();
    fd.append('proof', f, f.name);
    void act(id, () => api.upload(`/api/deposit-requests/${id}/proof`, fd));
  };

  const mine = (requests ?? []).filter((r) => (wire ? r.method !== 'card' : r.method === 'card'));

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,500px)] 2xl:grid-cols-[minmax(0,1fr)_minmax(0,564px)]">
      <Card>
        <form onSubmit={submit} className="flex flex-col gap-7">
          <div>
            <H1>{wire ? 'Deposit by wire transfer' : 'Deposit by card'}</H1>
            <p className="m-0 mt-2 text-[16px] text-muted md:text-[17px]">
              {wire
                ? "Choose the bank or money app you're sending from and book a call. Your account manager calls with our bank details, then you upload proof of the transfer here."
                : 'Book a call. Your account manager sends you a secure card payment link. Never share card numbers by phone, chat or email.'}
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <Field label="Country" htmlFor="dr-country">
              <select id="dr-country" className="field" value={country} onChange={(e) => pickCountry(e.target.value)}>
                <option value="">Choose a country</option>
                {countryNames.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                    {isRestrictedCountry(c.code) ? ' (not available)' : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Currency" htmlFor="dr-currency">
              <select id="dr-currency" className="field" value={currency} onChange={(e) => setCurrency(e.target.value as DepositCurrency)}>
                {DEPOSIT_CURRENCIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </Field>
          </div>
          {restricted && (
            <Alert>
              <b>We can't accept deposits from {countryName(country)}.</b> Our services aren't available in {RESTRICTED_COUNTRY_NAMES}. Choose another country if you're sending from somewhere else.
            </Alert>
          )}
          <fieldset disabled={restricted || !country} className="m-0 flex min-w-0 flex-col gap-7 border-0 p-0 disabled:opacity-45">
            {wire && (
              <div className="flex flex-col gap-2.5">
                <span className="text-[14px] font-semibold">Your bank</span>
                <BankPicker country={country} countryName={countryName(country)} value={bank} other={otherBank} onChange={setBank} onOther={setOtherBank} />
              </div>
            )}
            <Field
              label="Amount"
              htmlFor="dr-amount"
              error={belowMin ? `The minimum is $${min}${currency === 'USD' ? '' : ' (estimated in USD)'}` : null}
              hint={
                currency === 'USD' ? (
                  `Minimum $${min}. Credited in USDT, 1 USDT for every $1 that arrives.`
                ) : usd !== null && amt > 0 ? (
                  <>
                    We estimate <b className="num font-medium text-fg">${fmt(D.from(usd.toFixed(2)), 2)}</b> credited at today's rate. The final amount depends on what arrives.
                  </>
                ) : (
                  `Minimum $${min}. Credited in USDT at the rate on arrival.`
                )
              }
            >
              <div className="flex overflow-hidden rounded-[14px] bg-track">
                <NumInput
                  id="dr-amount"
                  required
                  className="num min-w-0 flex-1 bg-transparent px-3.5 py-3 text-[22px] font-medium outline-none"
                  placeholder="0.00"
                  value={amount}
                  onValue={(x) => /^\d*\.?\d{0,2}$/.test(x) && setAmount(x)}
                />
                <span className="num grid place-items-center border-l border-line-strong px-4 text-[15px] font-semibold text-muted">{currency}</span>
              </div>
            </Field>
            <div className="flex flex-col gap-2.5">
              <span className="text-[14px] font-semibold">Book a call with your account manager</span>
              <CallPicker
                tz={tz}
                value={call}
                onChange={(c) => {
                  if (c.callAt !== call.callAt) setTimeTouched(true);
                  setCall(c);
                }}
              />
            </div>
          </fieldset>
          {compliance && amt > 0 && (
            <ComplianceChecks
              c={compliance}
              usd={usd ?? amt}
              poa={poa}
              onPoa={(f) => {
                setMsg(null);
                setPoa(f);
              }}
              onAmount={(x) => setAmount(String(currency === 'USD' || !rate ? x : Math.floor((x / Number(rate)) * 100) / 100))}
              onError={(t) => setMsg({ ok: false, text: t })}
            />
          )}
          <Notice msg={msg} onClose={() => setMsg(null)} />
          <PillButton type="submit" size="lg" busy={busy === 'submit'} disabled={!ready}>
            {wire ? 'Request wire transfer' : 'Request card payment'}
          </PillButton>
        </form>
      </Card>

      <div className="flex min-w-0 flex-col gap-8">
        <Card>
          <H2>What happens next</H2>
          <ol className="m-0 mt-6 flex list-none flex-col p-0">
            {STEPS[method].map(([t, d], i) => (
              <li key={t} className="relative grid grid-cols-[28px_minmax(0,1fr)] gap-3 pb-4 last:pb-0">
                {i < STEPS[method].length - 1 && <span aria-hidden className="absolute top-7 bottom-0.5 left-[13px] w-0.5 bg-line" />}
                <span className={`num grid size-7 place-items-center rounded-full text-[13px] font-semibold ${i === 0 ? 'bg-up text-white' : 'bg-track text-muted'}`}>{i + 1}</span>
                <div>
                  <b className="block text-[15px] font-semibold">{t}</b>
                  <span className="text-[14px] text-muted">{d}</span>
                </div>
              </li>
            ))}
          </ol>
        </Card>
        <Card>
          <H2>{wire ? 'Your wire requests' : 'Your card requests'}</H2>
          <div className="mt-6">
            <List empty={wire ? 'No wire requests yet' : 'No card requests yet'}>
              {mine.map((r) => (
                <RequestRow key={r.id} r={r} tz={tz} busy={busy === r.id} onCancel={() => void act(r.id, () => api.post(`/api/deposit-requests/${r.id}/cancel`))} onProof={(f) => uploadProof(r.id, f)} />
              ))}
            </List>
          </div>
        </Card>
      </div>
    </div>
  );
}

function RequestRow({ r, tz, busy, onCancel, onProof }: { r: DepositRequest; tz: string; busy: boolean; onCancel: () => void; onProof: (f: File) => void }) {
  const [showDetails, setShowDetails] = useState(false);
  const [tone, label] = [REQUEST_STATUS[r.status][1], REQUEST_STATUS[r.status][0]];
  const credited = r.status === 'credited' && r.amountReceived;
  const proofInput = (text: string) => (
    <label className={`inline-flex cursor-pointer items-center gap-2 rounded-full bg-pill px-4 py-2 text-[13px] font-semibold ${busy ? 'pointer-events-none opacity-50' : ''}`}>
      {busy ? <Spinner /> : <AIcon name="upload" size={16} />}
      {text}
      <input
        type="file"
        accept="image/jpeg,image/png,application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) onProof(f);
        }}
      />
    </label>
  );
  return (
    <div className="flex flex-col gap-2.5 py-1">
      <Row
        mono
        icon={<AIcon name="down" />}
        iconClass={credited ? 'bg-up text-white' : undefined}
        title={credited ? `+$${fmt(r.amountReceived!, 2)}` : money(r.amount, r.currency)}
        sub={
          <span title={r.reason ?? undefined}>
            {r.number} · {r.method === 'card' ? 'Card' : (r.bankName ?? 'Wire')} · {when(r.createdAt)}
            {r.status === 'rejected' && r.reason ? ` · ${r.reason}` : ''}
          </span>
        }
        right={<Chip tone={tone}>{label}</Chip>}
      />
      {r.callAt && ['pending', 'details_sent', 'link_sent'].includes(r.status) && (
        <span className="pl-[56px] text-[13px]">
          {formatCallTime(Date.parse(r.callAt), tz)} · {contactLabel(r.contactMethod)}
        </span>
      )}
      {(r.status === 'pending' || r.status === 'details_sent' || r.status === 'link_sent' || r.status === 'proof_uploaded') && (
        <div className="flex flex-wrap items-center gap-2 pl-[56px]">
          {r.status === 'details_sent' && r.detailsMessage && (
            <button type="button" className="rounded-full bg-pill px-4 py-2 text-[13px] font-semibold" onClick={() => setShowDetails((s) => !s)}>
              {showDetails ? 'Hide bank details' : 'View bank details'}
            </button>
          )}
          {r.status === 'details_sent' && proofInput('Upload proof of payment')}
          {r.status === 'proof_uploaded' && proofInput('Replace proof')}
          {r.status === 'link_sent' && r.paymentLink && (
            <a href={r.paymentLink} target="_blank" rel="noopener noreferrer" className="rounded-full bg-fg px-4 py-2 text-[13px] font-semibold text-bg no-underline">
              Open secure payment link
            </a>
          )}
          {r.status === 'pending' && (
            <button type="button" disabled={busy} onClick={onCancel} className="text-[13px] font-semibold text-down">
              Cancel request
            </button>
          )}
          {r.status === 'details_sent' && <span className="text-[12px] text-muted">JPG, PNG or PDF, 10 MB</span>}
        </div>
      )}
      {showDetails && r.detailsMessage && <pre className="num m-0 ml-[56px] rounded-[12px] bg-track px-4 py-3 text-[14px] break-words whitespace-pre-wrap">{r.detailsMessage}</pre>}
    </div>
  );
}
